/**
 * Shared timeline glyphs (W1-15 timeline-host; contract `TimelineGlyphs` in render/types.ts, DESIGN.md §4.6-4.7).
 * Used by the note lane (W2-03), the motion columns (W2-04a) and the curve editor (W2-04b) so a key, a jump key,
 * a stay segment and a density band look the same everywhere.
 *
 * Colours are the caller's: every glyph draws with the context's current `fillStyle` / `strokeStyle` /
 * `lineWidth` (callers set them from the timeline theme tokens). Each glyph saves and restores the context state
 * it changes (path, line dash, alpha). Coordinates are CSS px in the column (the host pre-scales by the device
 * pixel ratio).
 *
 *   keyDiamond   <>   a keyframe; filled = selected (fill + outline), hollow = outline only.
 *   jumpKey      <|   the ARRIVE half of a jump key (arriveLeft = true): a half-diamond pointing left whose flat
 *                |>   edge sits on x; the LEAVE half (arriveLeft = false) points right. A jump key draws both
 *                     halves at their own values, x(vIn) and x(vOut), so each can be dragged (DESIGN.md §4.7).
 *   holdSegment  :    a "Stay, then jump" segment: a vertical dashed line from y0 to y1 with short end caps.
 *   densityBand  ###  a band [x0, x1] x [y, y + h] whose opacity grows with `density` (items per pixel row,
 *                     logarithmic, saturating at DENSITY_SATURATION), for LOD bands (lod.ts) and minimaps.
 *
 * Additive helpers: `hatchRect` (hatched fakes, "opacity < 0" bands), `densityAlpha`, `KEY_SIZE`.
 *
 * Note glyph shapes (`NoteGlyphs`, ARCHITECTURE.md §3.2 "shared key/note/hold glyphs"; DESIGN.md §4.6 "skin colours
 * and shapes"), shared by the lane, its paste ghosts and the generator ghosts so a kind reads the same everywhere,
 * not by colour alone. A glyph is centred on its note time `y` and spans [cx - halfW, cx + halfW]:
 *
 *   tap    [=====]     a bar, NOTE_GLYPH_H.tap px thick;
 *   drag   [-----]     a thin bar (NOTE_GLYPH_H.drag);
 *   flick  [==^==]     a bar with an arrow notch: a point on its later (upper) edge and a matching notch in its
 *                      earlier edge, so the bar reads as an arrow pointing along time;
 *   hold   [=====]     head bar at the head time, a body up to the end time (fill at `bodyAlpha`) and a thin tail
 *          |#####|     cap at the end (`holdGlyph`); `noteGlyph(..., 'hold', ...)` draws the head alone.
 *          [=====]
 * Lane-only decorations (simultaneous double outlines, hatched fakes via `hatchRect`, side chevrons, off-range
 * arrows, count badges) stay with the lane drawing (laneDraw.ts); `noteGlyphPath` lets it clip or outline a shape.
 */

import type { TimelineGlyphs } from '../types';

/** Default key glyph size (diamond width and height) in CSS px. */
export const KEY_SIZE = 9;
/** Items per pixel row at which a density band reaches full opacity. */
export const DENSITY_SATURATION = 8;
/** Opacity of a band with the faintest non-zero density. */
const DENSITY_MIN_ALPHA = 0.18;

/** Opacity for a density (items per pixel row): 0 for none, DENSITY_MIN_ALPHA..1 logarithmically. */
export function densityAlpha(density: number): number {
  if (!(density > 0)) return 0;
  const t = Math.log2(1 + density) / Math.log2(1 + DENSITY_SATURATION);
  return Math.min(1, DENSITY_MIN_ALPHA + (1 - DENSITY_MIN_ALPHA) * t);
}

function diamondPath(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + r, y);
  ctx.lineTo(x, y + r);
  ctx.lineTo(x - r, y);
  ctx.closePath();
}

export const Glyphs: TimelineGlyphs = {
  keyDiamond(ctx, x, y, size, filled) {
    const r = Math.max(1, size / 2);
    ctx.save();
    diamondPath(ctx, x, y, r);
    if (filled) ctx.fill();
    ctx.stroke();
    ctx.restore();
  },

  jumpKey(ctx, x, y, size, arriveLeft) {
    const r = Math.max(1, size / 2);
    const dir = arriveLeft ? -1 : 1;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, y - r);
    ctx.lineTo(x + dir * r, y);
    ctx.lineTo(x, y + r);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  },

  holdSegment(ctx, x, y0, y1) {
    const top = Math.min(y0, y1);
    const bottom = Math.max(y0, y1);
    const cap = 3;
    ctx.save();
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(x, bottom);
    ctx.lineTo(x, top);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(x - cap, top);
    ctx.lineTo(x + cap, top);
    ctx.moveTo(x - cap, bottom);
    ctx.lineTo(x + cap, bottom);
    ctx.stroke();
    ctx.restore();
  },

  densityBand(ctx, x0, x1, y, h, density) {
    const a = densityAlpha(density);
    if (a <= 0 || h <= 0) return;
    const left = Math.min(x0, x1);
    const w = Math.abs(x1 - x0);
    if (w <= 0) return;
    ctx.save();
    ctx.globalAlpha *= a;
    ctx.fillRect(left, y, w, h);
    ctx.restore();
  },
};

/**
 * Diagonal hatching inside a rectangle with the current strokeStyle (hatched fake notes, the opacity < 0 band of
 * the Opacity column). `spacing` is the distance between stripes in CSS px.
 */
export function hatchRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  spacing = 6,
): void {
  if (w <= 0 || h <= 0) return;
  const step = Math.max(2, spacing);
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.beginPath();
  for (let d = -h; d < w; d += step) {
    ctx.moveTo(x + d, y + h);
    ctx.lineTo(x + d + h, y);
  }
  ctx.stroke();
  ctx.restore();
}

// ---------------------------------------------------------------------------------------------------------------
// Note glyph shapes
// ---------------------------------------------------------------------------------------------------------------

export type NoteGlyphKind = 'tap' | 'drag' | 'flick' | 'hold';

/** Glyph thickness along the time axis, CSS px (the lane's hit geometry uses the same values). */
export const NOTE_GLYPH_H: Readonly<Record<NoteGlyphKind, number>> = { tap: 8, drag: 5, flick: 8, hold: 8 };
/** Thickness of a hold's tail cap, CSS px. */
export const HOLD_TAIL_H = 2;
/** Default opacity of a hold body relative to its head. */
export const HOLD_BODY_ALPHA = 0.55;

export interface NoteGlyphOptions {
  /** Thickness along the time axis (default NOTE_GLYPH_H[kind]). */
  readonly h?: number;
  /** Fill with the current fillStyle (default true). */
  readonly fill?: boolean;
  /** Outline with the current strokeStyle (default true). */
  readonly stroke?: boolean;
}

export interface HoldGlyphOptions extends NoteGlyphOptions {
  /** Body opacity relative to the context's alpha (default HOLD_BODY_ALPHA). */
  readonly bodyAlpha?: number;
}

function glyphH(kind: NoteGlyphKind, h: number | undefined): number {
  return h !== undefined && Number.isFinite(h) && h > 0 ? h : NOTE_GLYPH_H[kind];
}

/**
 * Builds (does not paint) the outline path of a note glyph centred at (cx, y), half width `halfW`, thickness `h`
 * (default NOTE_GLYPH_H[kind]). 'hold' is its head (a tap-shaped bar). For clipping (hatched fakes), hit outlines
 * and selection rings.
 */
export function noteGlyphPath(
  ctx: CanvasRenderingContext2D,
  kind: NoteGlyphKind,
  cx: number,
  y: number,
  halfW: number,
  h?: number,
): void {
  const hw = Math.max(0.5, halfW);
  const half = glyphH(kind, h) / 2;
  const top = y - half;
  const bottom = y + half;
  ctx.beginPath();
  if (kind !== 'flick') {
    ctx.rect(cx - hw, top, 2 * hw, bottom - top);
    return;
  }
  // Arrow notch: a point rising from the later (upper) edge and the same notch cut into the earlier edge.
  const rise = half * 1.2;
  const q = Math.min(hw * 0.5, half * 2.4);
  ctx.moveTo(cx - hw, bottom);
  ctx.lineTo(cx - hw, top);
  ctx.lineTo(cx - q, top);
  ctx.lineTo(cx, top - rise);
  ctx.lineTo(cx + q, top);
  ctx.lineTo(cx + hw, top);
  ctx.lineTo(cx + hw, bottom);
  ctx.lineTo(cx + q, bottom);
  ctx.lineTo(cx, bottom - rise);
  ctx.lineTo(cx - q, bottom);
  ctx.closePath();
}

/** Paints a tap / drag / flick glyph, or a hold's head, with the current fill and stroke styles. */
export function noteGlyph(
  ctx: CanvasRenderingContext2D,
  kind: NoteGlyphKind,
  cx: number,
  y: number,
  halfW: number,
  opts: NoteGlyphOptions = {},
): void {
  ctx.save();
  noteGlyphPath(ctx, kind, cx, y, halfW, opts.h);
  if (opts.fill !== false) ctx.fill();
  if (opts.stroke !== false) ctx.stroke();
  ctx.restore();
}

/**
 * Paints a hold: the body from the head time (yHead) to the end time (yTail; time flows up, so yTail <= yHead),
 * at `bodyAlpha`, a thin tail cap at yTail and the head bar at yHead on top. A zero-length hold is its head alone.
 */
export function holdGlyph(
  ctx: CanvasRenderingContext2D,
  cx: number,
  yHead: number,
  yTail: number,
  halfW: number,
  opts: HoldGlyphOptions = {},
): void {
  const hw = Math.max(0.5, halfW);
  const top = Math.min(yHead, yTail);
  const bottom = Math.max(yHead, yTail);
  const fill = opts.fill !== false;
  const stroke = opts.stroke !== false;
  ctx.save();
  if (bottom - top > 0) {
    const a = opts.bodyAlpha ?? HOLD_BODY_ALPHA;
    ctx.beginPath();
    ctx.rect(cx - hw, top, 2 * hw, bottom - top);
    if (fill) {
      ctx.save();
      ctx.globalAlpha *= Math.min(1, Math.max(0, a));
      ctx.fill();
      ctx.restore();
    }
    if (stroke) ctx.stroke();
    // Tail cap at the end time.
    ctx.beginPath();
    ctx.rect(cx - hw, top - HOLD_TAIL_H / 2, 2 * hw, HOLD_TAIL_H);
    if (fill) ctx.fill();
    if (stroke) ctx.stroke();
  }
  noteGlyphPath(ctx, 'hold', cx, yHead, hw, opts.h);
  if (fill) ctx.fill();
  if (stroke) ctx.stroke();
  ctx.restore();
}

/** The note glyph shapes as one object (lane, paste ghosts, generator ghosts). */
export const NoteGlyphs = Object.freeze({ path: noteGlyphPath, note: noteGlyph, hold: holdGlyph });
